
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { BloodSugarChart } from './BloodSugarChart';
import { BloodSugarHeader } from './BloodSugarHeader';
import { 
  generateBloodSugarData,
  getCurrentBloodSugar
} from '@/utils/bloodSugarCalculation';

interface BloodSugarGraphProps {
  timeElapsed: number;
  totalDuration: number;
}

export const BloodSugarGraph: React.FC<BloodSugarGraphProps> = ({ 
  timeElapsed, 
  totalDuration 
}) => {
  // Convert seconds to hours
  const hoursElapsed = timeElapsed / 3600;
  const [bloodSugarData, setBloodSugarData] = useState<Array<{hour: number, value: number}>>([]);
  const [currentValue, setCurrentValue] = useState<number>(0);
  
  // Generate blood sugar data based on fasting time
  useEffect(() => {
    const data = generateBloodSugarData(hoursElapsed, totalDuration);
    setBloodSugarData(data);
    
    // Set initial current blood sugar value
    const initialValue = getCurrentBloodSugar(data, hoursElapsed);
    setCurrentValue(initialValue);
    
    // Update data periodically to simulate real-time changes
    const interval = setInterval(() => {
      const newData = generateBloodSugarData(hoursElapsed, totalDuration);
      setBloodSugarData(newData);
      
      // Add small fluctuation to current value for real-time effect
      const fluctuation = (Math.random() * 2) - 1; // -1 to +1
      setCurrentValue(prev => {
        const newValue = Math.round(prev + fluctuation);
        return Math.max(65, Math.min(140, newValue));
      });
    }, 30000);
    
    return () => clearInterval(interval);
  }, [hoursElapsed, totalDuration]);
  
  // Add small real-time fluctuations
  useEffect(() => {
    const smallFluctuation = setInterval(() => {
      // Tiny fluctuations for real-time effect
      const tinyChange = (Math.random() * 0.6) - 0.3; // -0.3 to +0.3
      setCurrentValue(prev => {
        const newValue = Math.round((prev + tinyChange) * 10) / 10;
        return Math.max(65, Math.min(140, newValue));
      });
    }, 5000);
    
    return () => clearInterval(smallFluctuation);
  }, []);
  
  return (
    <Card className="w-full border-fasttrack-light-blue/30 mt-4">
      <BloodSugarHeader currentValue={currentValue} />
      <CardContent>
        <BloodSugarChart 
          bloodSugarData={bloodSugarData} 
          hoursElapsed={hoursElapsed} 
        />
        <div className="text-xs text-muted-foreground mt-2 text-center">
          Estimated blood sugar levels during your fast
        </div>
      </CardContent>
    </Card>
  );
};
